import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import usePullToRefresh from '../hooks/usePullToRefresh';

function Help() {
  const navigate = useNavigate();
  const { logout, isAdmin, isDriver, isSuperAdmin } = useAuth();
  const { getAppName } = useConfig();
  const [activeTab, setActiveTab] = useState('getting-started');
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFullManual, setShowFullManual] = useState(false);

  // Refresh function for pull-to-refresh (resets view state)
  const refreshHelp = useCallback(async () => {
    setSearchQuery('');
    setExpandedSections({});
    // Small delay to show refresh animation
    await new Promise(resolve => setTimeout(resolve, 300));
  }, []);

  const { PullToRefreshIndicator } = usePullToRefresh(refreshHelp);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewFullManual = () => {
    setShowFullManual(true);
    // Scroll to top when switching to full manual view
    window.scrollTo(0, 0);
  };

  const handleBackToHelp = () => {
    setShowFullManual(false);
    window.scrollTo(0, 0);
  };

  const handlePrintManual = () => {
    window.print();
  };

  // Accordion Section Component
  const AccordionSection = ({ id, title, children }) => {
    const isExpanded = expandedSections[id];
    const contentString = typeof children === 'string' ? children : '';
    const matchesSearch = !searchQuery ||
      title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contentString.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch && searchQuery) return null;

    return (
      <div className="accordion-section">
        <button
          className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => toggleSection(id)}
        >
          <span>{title}</span>
          <span className="accordion-icon">{isExpanded ? '−' : '+'}</span>
        </button>
        {isExpanded && (
          <div className="accordion-content">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="help-page">
      {/* Header */}
      <header className="app-header no-print">
        <div className="app-header-content">
          <span className="app-logo">{getAppName()}</span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <span className="nav-link help-link active" title="Documentation">?</span>
            {isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <span className="mobile-nav-link active">Help</span>
            {isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <PullToRefreshIndicator />

      <main className="help-main">
        {showFullManual ? (
          /* Full Manual View - All content expanded for printing */
          <div className="full-manual">
            <div className="full-manual-header no-print">
              <button className="btn btn-secondary" onClick={handleBackToHelp}>
                ← Back to Documentation
              </button>
              <button className="btn btn-primary" onClick={handlePrintManual}>
                Print / Save as PDF
              </button>
            </div>

            <div className="manual-title-section">
              <h1>LawnTrack User Manual</h1>
              <p className="manual-version">Version 2.8.0 • Complete Documentation</p>
            </div>

            <div className="manual-toc">
              <h2>Table of Contents</h2>
              <ol>
                <li><a href="#manual-getting-started">Getting Started</a></li>
                <li><a href="#manual-customers">Customer Management</a></li>
                <li><a href="#manual-routes">Routes</a></li>
                <li><a href="#manual-service-mode">Service Mode</a></li>
                <li><a href="#manual-billing">Billing & Payments</a></li>
                <li><a href="#manual-history">Service History</a></li>
                <li><a href="#manual-expenses">Expenses</a></li>
                <li><a href="#manual-reports">Reports</a></li>
                <li><a href="#manual-settings">Settings</a></li>
                <li><a href="#manual-referrals">Referral Discounts</a></li>
                <li><a href="#manual-faq">FAQ</a></li>
              </ol>
            </div>

            <section id="manual-getting-started" className="manual-section">
              <h2>1. Getting Started</h2>

              <h3>Creating Your Account</h3>
              <ol>
                <li>Visit <strong>lawntrack.pro</strong></li>
                <li>Click <strong>Start Free Trial</strong></li>
                <li>Enter your company information (name, email, phone, password)</li>
                <li>Your account starts with <strong>1 Free Service Visit</strong> - full access to try all features</li>
              </ol>

              <h3>Dashboard Overview</h3>
              <p>Your Dashboard displays key business metrics:</p>
              <ul>
                <li><strong>Total Customers</strong> - Number of customers in your account</li>
                <li><strong>Active Routes</strong> - Routes currently in progress</li>
                <li><strong>Total Routes</strong> - All routes created</li>
                <li><strong>Weather Widget</strong> - Current conditions and forecasts (GPS or home location)</li>
              </ul>

              <h3>Navigation</h3>
              <ul>
                <li><strong>Dashboard</strong> - Business overview and weather</li>
                <li><strong>Customers</strong> - Manage customer accounts</li>
                <li><strong>Routes</strong> - Create and manage service routes</li>
                <li><strong>History</strong> - View completed services</li>
                <li><strong>Expenses</strong> - Track business expenses (Pro tier)</li>
                <li><strong>Reports</strong> - Profit & Loss analysis (Pro tier)</li>
                <li><strong>Service Mode</strong> - Quick lawn service management</li>
                <li><strong>Settings</strong> - Company and account configuration</li>
              </ul>

              <h3>Mobile App (PWA)</h3>
              <p>Install LawnTrack on your phone:</p>
              <ol>
                <li>Visit lawntrack.pro on your mobile browser</li>
                <li>Tap your browser's menu</li>
                <li>Select "Add to Home Screen" or "Install App"</li>
              </ol>
            </section>

            <section id="manual-customers" className="manual-section">
              <h2>2. Customer Management</h2>

              <h3>Adding Customers</h3>
              <ol>
                <li>Navigate to <strong>Customers</strong></li>
                <li>Click <strong>+ Add Customer</strong></li>
                <li>Fill in customer details and click <strong>Save</strong></li>
              </ol>

              <h3>Customer Fields</h3>
              <ul>
                <li><strong>First Name / Last Name</strong> - Required for Square invoicing</li>
                <li><strong>Email</strong> - Required for sending invoices</li>
                <li><strong>Phone</strong> - Tap to call or text</li>
                <li><strong>Service Address</strong> - Where you provide service</li>
                <li><strong>Pricing Tier</strong> - Select from your configured tiers</li>
                <li><strong>Custom Pricing</strong> - Override tier pricing</li>
                <li><strong>Fertilizer Preference</strong> - Always, Never, or Ask each time</li>
                <li><strong>Lead Source</strong> - How they found you</li>
                <li><strong>Referred By</strong> - Select referring customer for referral discounts</li>
              </ul>

              <h3>Multiple Properties</h3>
              <p>Customers can have multiple service addresses. Each property can have its own pricing tier and fertilizer preference.</p>

              <h3>Discounts</h3>
              <ul>
                <li><strong>Percentage Discount</strong> - e.g., 10% off</li>
                <li><strong>Dollar Discount</strong> - e.g., $5 off per service</li>
              </ul>
            </section>

            <section id="manual-routes" className="manual-section">
              <h2>3. Routes</h2>

              <h3>Creating a Route</h3>
              <ol>
                <li>Navigate to <strong>Routes</strong></li>
                <li>Click <strong>+ Create Route</strong></li>
                <li>Enter route name and optional starting/ending addresses</li>
                <li>Configure invoice settings if using Square invoicing</li>
                <li>Add customers as stops</li>
              </ol>

              <h3>Managing Stops</h3>
              <ul>
                <li><strong>Add stops</strong> - Select customers to add</li>
                <li><strong>Reorder</strong> - Drag and drop to optimize route order</li>
                <li><strong>Remove</strong> - Click X to remove a stop</li>
              </ul>

              <h3>Route Statuses</h3>
              <ul>
                <li><strong>Active</strong> - Ready to begin</li>
                <li><strong>In Progress</strong> - Currently being serviced</li>
                <li><strong>Completed</strong> - All stops finished</li>
                <li><strong>Archived</strong> - Historical record</li>
              </ul>

              <h3>Invoice Settings per Route</h3>
              <p>Customize invoices using variables:</p>
              <ul>
                <li><code>{'{route_name}'}</code> - Route name</li>
                <li><code>{'{service_date}'}</code> - Date of service</li>
                <li><code>{'{customer_name}'}</code> - Customer name</li>
              </ul>
            </section>

            <section id="manual-service-mode" className="manual-section">
              <h2>4. Service Mode</h2>

              <h3>Starting Service Mode</h3>
              <ol>
                <li>Click <strong>Service Mode</strong> in navigation</li>
                <li>Select an existing route or create a new service visit</li>
                <li>Enter service details for the visit</li>
              </ol>

              <h3>Completing a Stop</h3>
              <ol>
                <li>Click the stop to open completion dialog</li>
                <li>Confirm service details</li>
                <li>Select if fertilizer was applied</li>
                <li>Choose payment method</li>
                <li>Click <strong>Complete Stop</strong></li>
              </ol>

              <h3>Payment Options</h3>
              <ul>
                <li><strong>Square (Pay Now)</strong> - Process card payment immediately</li>
                <li><strong>Venmo, Cash, CashApp, Check, Zelle</strong> - Mark as paid</li>
                <li><strong>Unpaid</strong> - Leave for later invoicing via Bill All</li>
              </ul>

              <h3>Visit ID Importance</h3>
              <p><strong>Critical:</strong> Each route should have a Visit ID. This is required for:</p>
              <ul>
                <li>Referral discount tracking (discounts only apply within same visit)</li>
                <li>Revenue and services reporting per visit</li>
                <li>Billing purposes</li>
              </ul>
            </section>

            <section id="manual-billing" className="manual-section">
              <h2>5. Billing & Payments</h2>

              <h3>Bill All Feature</h3>
              <ol>
                <li>Complete all stops on a route</li>
                <li>Click <strong>Bill All</strong> on the route card</li>
                <li>Invoices are sent to all unpaid customers with email addresses</li>
              </ol>

              <h3>Invoice Line Items</h3>
              <ul>
                <li><strong>Lawn Care</strong> - Base service price</li>
                <li><strong>Fertilizer</strong> - Salt price (if applied)</li>
                <li>Discounts and referral discounts</li>
              </ul>

              <h3>Customer Requirements for Invoicing</h3>
              <ul>
                <li>First and Last Name (required for Square)</li>
                <li>Email Address (where invoice is sent)</li>
              </ul>
            </section>

            <section id="manual-history" className="manual-section">
              <h2>6. Service History</h2>

              <h3>Viewing History</h3>
              <p>Filter completed services by date range, customer, or payment status.</p>

              <h3>Payment Status</h3>
              <ul>
                <li><strong>Unpaid</strong> - Awaiting payment</li>
                <li><strong>Paid</strong> - Payment received</li>
                <li><strong>Partial</strong> - Partial payment received</li>
              </ul>

              <h3>Recording Payments</h3>
              <ol>
                <li>Find the service in history</li>
                <li>Click <strong>Mark Paid</strong></li>
                <li>Select payment method</li>
              </ol>
            </section>

            <section id="manual-expenses" className="manual-section">
              <h2>7. Expenses (Pro Tier)</h2>

              <h3>Adding Expenses</h3>
              <ol>
                <li>Navigate to <strong>Expenses</strong></li>
                <li>Click <strong>+ Add Expense</strong></li>
                <li>Enter date, amount, category, description</li>
                <li>Optionally link to a route</li>
              </ol>

              <h3>Categories</h3>
              <ul>
                <li><strong>Helper Pay</strong> - Contractor payments</li>
                <li><strong>Materials</strong> - Fertilizer, seed, supplies</li>
                <li><strong>Fuel</strong> - Gas, diesel</li>
                <li><strong>Equipment</strong> - Tools, parts</li>
                <li><strong>Repairs</strong> - Maintenance</li>
                <li><strong>Other</strong> - Miscellaneous</li>
              </ul>
            </section>

            <section id="manual-reports" className="manual-section">
              <h2>8. Reports (Pro Tier)</h2>

              <h3>Profit & Loss Analysis</h3>
              <p>Date range options: This Month, Last Month, This Season (Oct-Apr), Custom</p>

              <h3>Metrics</h3>
              <ul>
                <li><strong>Total Revenue</strong> - Sum of all completed services</li>
                <li><strong>Total Expenses</strong> - Sum of all expenses</li>
                <li><strong>Net Profit</strong> - Revenue minus expenses</li>
                <li><strong>Profit Margin</strong> - Net profit as percentage</li>
              </ul>

              <h3>Breakdowns</h3>
              <ul>
                <li>Revenue by payment status (Paid/Unpaid)</li>
                <li>Revenue by payment method</li>
                <li>Expenses by category</li>
              </ul>
            </section>

            <section id="manual-settings" className="manual-section">
              <h2>9. Settings</h2>

              <h3>Company Information</h3>
              <ul>
                <li>Company name, phone, email, address</li>
                <li>Logo upload (appears on invoices)</li>
                <li>Company code for public intake form</li>
              </ul>

              <h3>Pricing Tiers</h3>
              <p>Create customizable pricing categories with name, base price, and fertilizer price.</p>

              <h3>Invoice Defaults</h3>
              <p>Set company-wide invoice title and message using variables.</p>

              <h3>Payment Methods</h3>
              <p>Enable/disable payment methods shown in dropdowns.</p>

              <h3>Weather Location</h3>
              <ul>
                <li><strong>GPS Location</strong> - Weather based on current location</li>
                <li><strong>Home Location</strong> - Set a fixed city/state</li>
              </ul>
            </section>

            <section id="manual-referrals" className="manual-section">
              <h2>10. Referral Discounts</h2>

              <h3>How Referrals Work</h3>
              <p>When a customer refers someone who also receives service in the same visit, the referring customer gets a discount.</p>

              <h3>Setup</h3>
              <ol>
                <li>Go to <strong>Settings → Referral Discount Settings</strong></li>
                <li>Enable referral discounts</li>
                <li>Set discount per referral (e.g., $5.00)</li>
                <li>Set minimum price floor</li>
              </ol>

              <h3>Example</h3>
              <p>If discount is $5/referral and Customer A referred 2 people who got service in the same visit, Customer A gets $10 off.</p>

              <h3>Important</h3>
              <p>Referral discounts only apply when both referrer and referred customer are serviced in the same visit (Visit ID must be set).</p>
            </section>

            <section id="manual-faq" className="manual-section">
              <h2>11. Frequently Asked Questions</h2>

              <h3>How does the free trial work?</h3>
              <p>New accounts start with 1 Free Service Visit. After completing 1 service visit, you'll need to upgrade to continue.</p>

              <h3>How does pricing work?</h3>
              <ul>
                <li><strong>Basic</strong>: $10/month + $3 per visit (up to 25 customers)</li>
                <li><strong>Pro</strong>: $25/month + $2 per visit (up to 75 customers)</li>
                <li><strong>Premium</strong>: $49/month unlimited (up to 200 customers)</li>
                <li><strong>Enterprise</strong>: $99/month unlimited (unlimited customers)</li>
              </ul>

              <h3>Can a customer have multiple properties?</h3>
              <p>Yes! Each customer can have multiple service addresses with their own pricing.</p>

              <h3>Can customers sign up themselves?</h3>
              <p>Yes! Share your public intake form: https://lawntrack.pro/signup/YOUR-COMPANY-CODE</p>
            </section>

            <div className="manual-footer">
              <p>LawnTrack™ • © {new Date().getFullYear()} • support@lawntrack.pro</p>
            </div>
          </div>
        ) : (
          /* Regular Help Center View */
          <>
        {/* Page Title */}
        <div className="help-title-section">
          <h1 className="help-title">Documentation</h1>
          <p className="help-subtitle">Complete guide to using LawnTrack for your lawn care business</p>
        </div>

        {/* Search and Actions */}
        <div className="help-actions no-print">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}>×</button>
            )}
          </div>
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleViewFullManual}>
              View Full Manual
            </button>
            <button className="btn btn-secondary" onClick={handlePrint}>
              Print This Page
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="help-tabs no-print">
          <button
            className={`help-tab ${activeTab === 'getting-started' ? 'active' : ''}`}
            onClick={() => setActiveTab('getting-started')}
          >
            Getting Started
          </button>
          <button
            className={`help-tab ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            Features
          </button>
          <button
            className={`help-tab ${activeTab === 'faq' ? 'active' : ''}`}
            onClick={() => setActiveTab('faq')}
          >
            FAQ
          </button>
          <button
            className={`help-tab ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact
          </button>
        </div>

        {/* Content Area */}
        <div className="help-content">
          {/* Getting Started Tab */}
          {activeTab === 'getting-started' && (
            <div className="tab-content">
              <h2 className="section-title">Getting Started with LawnTrack</h2>
              <p className="intro-text">
                Welcome to LawnTrack! This comprehensive guide will help you set up your account and start managing
                your lawn care business efficiently.
              </p>

              <AccordionSection id="gs-account" title="Account Setup & First Login">
                <h4>Creating Your Account</h4>
                <ol>
                  <li>Visit <strong>lawntrack.pro</strong></li>
                  <li>Click <strong>Start Free Trial</strong></li>
                  <li>Enter your company information:
                    <ul>
                      <li>Company name</li>
                      <li>Your name, email, and phone</li>
                      <li>Create a password</li>
                    </ul>
                  </li>
                  <li>Your account starts with <strong>1 Free Service Visit</strong> - full access to try all features</li>
                </ol>

                <h4>First Login</h4>
                <p>After registering, you'll be taken to your Dashboard. We recommend completing these steps:</p>
                <ol>
                  <li><strong>Settings</strong> - Set up your pricing tiers and invoice defaults</li>
                  <li><strong>Customers</strong> - Add your customer list</li>
                  <li><strong>Routes</strong> - Organize customers into service routes</li>
                </ol>
              </AccordionSection>

              <AccordionSection id="gs-dashboard" title="Dashboard Overview">
                <h4>Key Metrics</h4>
                <p>Your Dashboard displays at-a-glance business metrics:</p>
                <ul>
                  <li><strong>Total Customers</strong> - Number of customers in your account</li>
                  <li><strong>Active Routes</strong> - Routes currently in progress</li>
                  <li><strong>Total Routes</strong> - All routes created</li>
                  <li><strong>Pending Stops</strong> - Stops awaiting service</li>
                </ul>

                <h4>Weather Widget</h4>
                <p>The weather widget shows current conditions and forecasts:</p>
                <ul>
                  <li>Current temperature and conditions</li>
                  <li>Weather forecasts</li>
                  <li>Toggle between GPS location and home location (set in Settings)</li>
                </ul>

                <h4>Quick Actions</h4>
                <p>Common actions available from Dashboard:</p>
                <ul>
                  <li>Create a new route</li>
                  <li>Add a customer</li>
                  <li>Enter Service Mode</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="gs-navigation" title="Navigation">
                <h4>Main Menu Items</h4>
                <ul>
                  <li><strong>Dashboard</strong> - Business overview and weather</li>
                  <li><strong>Customers</strong> - Manage customer accounts and properties</li>
                  <li><strong>Routes</strong> - Create and manage service routes</li>
                  <li><strong>History</strong> - View completed services and payments</li>
                  <li><strong>Expenses</strong> - Track business expenses (Pro tier)</li>
                  <li><strong>Reports</strong> - Profit & Loss analysis (Pro tier)</li>
                  <li><strong>Service Mode</strong> - Quick lawn service management</li>
                  <li><strong>Settings</strong> - Company and account configuration</li>
                  <li><strong>?</strong> - Help Center (you're here!)</li>
                </ul>

                <h4>Mobile Navigation</h4>
                <p>On mobile devices, tap the menu icon (three lines) to access the full navigation menu.</p>
              </AccordionSection>

              <AccordionSection id="gs-mobile" title="Mobile App (PWA)">
                <h4>Installing LawnTrack on Your Phone</h4>
                <p>LawnTrack works as a Progressive Web App (PWA) - use it like a native app:</p>
                <ol>
                  <li>Visit <strong>lawntrack.pro</strong> on your mobile browser</li>
                  <li>Log in to your account</li>
                  <li>Tap your browser's menu (usually three dots)</li>
                  <li>Select <strong>"Add to Home Screen"</strong> or <strong>"Install App"</strong></li>
                  <li>LawnTrack will appear on your home screen like any other app</li>
                </ol>

                <h4>Mobile Features</h4>
                <ul>
                  <li><strong>Pull-to-refresh</strong> - Pull down on any page to refresh data</li>
                  <li><strong>Tap phone numbers</strong> - Quick call or text customers</li>
                  <li><strong>Touch-friendly interface</strong> - Large buttons and swipe gestures</li>
                  <li><strong>Offline capable</strong> - Basic functionality when connection is spotty</li>
                </ul>
              </AccordionSection>

              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <h3>Add Your Customers</h3>
                  <p>Start by adding your customers with their service addresses and pricing information.</p>
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/customers')}>
                    Go to Customers
                  </button>
                </div>

                <div className="step-card">
                  <div className="step-number">2</div>
                  <h3>Set Up Pricing Tiers</h3>
                  <p>Configure your pricing tiers (e.g., Small, Medium, Large) with base and fertilizer prices.</p>
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/settings')}>
                    Go to Settings
                  </button>
                </div>

                <div className="step-card">
                  <div className="step-number">3</div>
                  <h3>Create Routes</h3>
                  <p>Organize customers into routes for efficient service during service.</p>
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/routes')}>
                    Go to Routes
                  </button>
                </div>

                <div className="step-card">
                  <div className="step-number">4</div>
                  <h3>Use Service Mode</h3>
                  <p>When it snows, use Service Mode to quickly service your routes and track payments.</p>
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/service-mode')}>
                    Go to Service Mode
                  </button>
                </div>
              </div>

              <div className="tip-box">
                <strong>Pro Tip:</strong> Set up your invoice defaults in Settings before your first service.
                This allows you to send professional invoices with one click using the Bill All feature.
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="tab-content">
              <h2 className="section-title">Feature Guide</h2>

              <AccordionSection id="customers" title="Customer Management">
                <h4>Adding Customers</h4>
                <ol>
                  <li>Navigate to the <strong>Customers</strong> page</li>
                  <li>Click <strong>+ Add Customer</strong></li>
                  <li>Fill in the customer form (see fields below)</li>
                  <li>Click <strong>Save Customer</strong></li>
                </ol>

                <h4>Customer Fields Explained</h4>
                <ul>
                  <li><strong>First Name / Last Name</strong> - Required for Square invoicing</li>
                  <li><strong>Email</strong> - Required for sending invoices</li>
                  <li><strong>Phone</strong> - Tap to call or text</li>
                  <li><strong>Service Address</strong> - Where you provide service</li>
                  <li><strong>Billing Address</strong> - Only if different from service address</li>
                  <li><strong>Service Type</strong> - Lawn Care or Both Services</li>
                  <li><strong>Pricing Tier</strong> - Select from your configured tiers</li>
                  <li><strong>Custom Pricing</strong> - Override tier pricing with specific amounts</li>
                  <li><strong>Fertilizer Preference</strong> - Always, Never, or Ask each time</li>
                  <li><strong>Lead Source</strong> - How they found you (Facebook, Google, Referral, etc.)</li>
                  <li><strong>Referred By</strong> - Select referring customer for referral discounts</li>
                  <li><strong>Notes</strong> - Special instructions or gate codes</li>
                </ul>

                <h4>Pricing Tiers vs Custom Pricing</h4>
                <p><strong>Pricing Tiers</strong> are predefined price categories you set up in Settings:</p>
                <ul>
                  <li>Each tier has a name, base price, and fertilizer price</li>
                  <li>Select a tier and prices auto-populate</li>
                  <li>Great for standardized pricing</li>
                </ul>
                <p><strong>Custom Pricing</strong> overrides the tier:</p>
                <ul>
                  <li>Check "Use Custom Pricing"</li>
                  <li>Enter specific base and fertilizer prices for this customer</li>
                  <li>Useful for negotiated rates or special circumstances</li>
                </ul>

                <h4>Multiple Properties</h4>
                <p>Customers can have multiple service addresses:</p>
                <ol>
                  <li>Open customer details</li>
                  <li>Click <strong>+ Add Property</strong></li>
                  <li>Enter property address and pricing</li>
                  <li>Each property can have its own tier, custom pricing, and fertilizer preference</li>
                </ol>

                <h4>Discounts</h4>
                <p>Apply discounts to individual customers:</p>
                <ul>
                  <li><strong>Percentage Discount</strong> - e.g., 10% off (applied to total)</li>
                  <li><strong>Dollar Discount</strong> - e.g., $5 off per service</li>
                  <li><strong>Discount Reason</strong> - Add a note explaining the discount</li>
                </ul>

                <h4>Lead Sources & Referrals</h4>
                <ul>
                  <li><strong>Lead Source</strong> - Track how customers find you (Facebook, Google, Yard Sign, Referral, etc.)</li>
                  <li><strong>Referred By</strong> - Link to the customer who referred them for referral discount tracking</li>
                </ul>

                <h4>Editing & Deleting Customers</h4>
                <ul>
                  <li><strong>Edit</strong> - Click the edit icon on any customer card</li>
                  <li><strong>Delete</strong> - Click delete icon (only if customer has no service history)</li>
                  <li><strong>Archive</strong> - Mark inactive customers without deleting history</li>
                </ul>

                <h4>Print Customer List</h4>
                <ol>
                  <li>Go to the <strong>Customers</strong> page</li>
                  <li>Click the <strong>Print</strong> button (printer icon)</li>
                  <li>A formatted list opens with company header, customer details, and property info</li>
                </ol>

                <h4>Phone Actions</h4>
                <p>Tap any phone number to see options:</p>
                <ul>
                  <li><strong>Call</strong> - Start a phone call</li>
                  <li><strong>Text</strong> - Open SMS message</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="routes" title="Routes">
                <h4>Creating a Route</h4>
                <ol>
                  <li>Navigate to <strong>Routes</strong></li>
                  <li>Click <strong>+ Create Route</strong></li>
                  <li>Enter a descriptive route name (e.g., "North Side", "Downtown")</li>
                  <li>Optionally set starting and ending addresses for navigation</li>
                  <li>Configure invoice settings if using Square invoicing</li>
                  <li>Click <strong>Save Route</strong></li>
                </ol>

                <h4>Adding Stops</h4>
                <ol>
                  <li>Open the route you want to edit</li>
                  <li>Click <strong>+ Add Stops</strong></li>
                  <li>Select customers from the list (checkboxes)</li>
                  <li>Click <strong>Add Selected</strong></li>
                  <li>For multi-property customers, each property appears as a separate stop</li>
                </ol>

                <h4>Reordering Stops (Drag and Drop)</h4>
                <p>Optimize your route order:</p>
                <ul>
                  <li>Click and hold the grip icon (⋮⋮) on any stop</li>
                  <li>Drag to the desired position</li>
                  <li>Release to drop</li>
                  <li>Order is saved automatically</li>
                </ul>

                <h4>Starting/Ending Locations</h4>
                <p>Optionally set addresses where you start and end:</p>
                <ul>
                  <li><strong>Starting Address</strong> - Where you begin (e.g., your shop)</li>
                  <li><strong>Ending Address</strong> - Where you finish</li>
                  <li>These appear in Google Maps navigation if configured</li>
                </ul>

                <h4>Invoice Settings per Route</h4>
                <p>Customize invoices for each route:</p>
                <ul>
                  <li><strong>Invoice Title</strong> - Custom title using variables:
                    <ul>
                      <li>{'{route_name}'} - Route name</li>
                      <li>{'{service_date}'} - Date of service</li>
                      <li>{'{customer_name}'} - Customer name</li>
                    </ul>
                  </li>
                  <li><strong>Invoice Message</strong> - Personal message on invoices</li>
                </ul>
                <p>If not set, company-wide defaults from Settings are used.</p>

                <h4>Route Statuses</h4>
                <ul>
                  <li><strong>Active (Not Started)</strong> - Route created, ready to begin</li>
                  <li><strong>In Progress</strong> - Currently being serviced</li>
                  <li><strong>Completed</strong> - All stops finished, ready for billing</li>
                  <li><strong>Archived</strong> - Historical record, hidden from active view</li>
                </ul>

                <h4>Payment Status Display</h4>
                <p>Route cards show payment summary:</p>
                <ul>
                  <li><strong>Green badge</strong> - "X Paid" (number of paid stops)</li>
                  <li><strong>Red badge</strong> - "X Unpaid" (number awaiting payment)</li>
                </ul>

                <h4>Viewing & Editing Routes</h4>
                <ul>
                  <li><strong>Click route card</strong> - View route details and stops</li>
                  <li><strong>Edit button</strong> - Modify route name, addresses, invoice settings</li>
                  <li><strong>Archive button</strong> - Move to archived routes</li>
                  <li><strong>Delete</strong> - Only available for routes with no service history</li>
                </ul>

                <h4>Sorting Routes</h4>
                <p>Toggle sort order using the sort button:</p>
                <ul>
                  <li><strong>Newest First</strong> - Most recently created at top</li>
                  <li><strong>Oldest First</strong> - Oldest routes at top</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="service-mode" title="Service Mode">
                <h4>What is Service Mode?</h4>
                <p>Service Mode is designed for quick, efficient service during active services. It streamlines
                the process of completing stops, tracking payments, and billing customers.</p>

                <h4>Starting a Route in Service Mode</h4>
                <ol>
                  <li>Click <strong>Service Mode</strong> in navigation</li>
                  <li>Select an existing route or create a new service visit</li>
                  <li>The route opens with all stops ready for service</li>
                  <li>Optional: Enter service details for the visit</li>
                </ol>

                <h4>Completing a Stop</h4>
                <p>When you arrive at a customer:</p>
                <ol>
                  <li>Click the stop to open completion dialog</li>
                  <li>Confirm or adjust <strong>Service Details</strong></li>
                  <li>Select <strong>Fertilizer Applied</strong> (Yes/No)</li>
                  <li>Choose <strong>Payment Method</strong></li>
                  <li>Add any notes (optional)</li>
                  <li>Click <strong>Complete Stop</strong></li>
                </ol>

                <h4>Skipping a Stop</h4>
                <p>If a customer doesn't need service:</p>
                <ol>
                  <li>Click the stop</li>
                  <li>Click <strong>Skip</strong></li>
                  <li>Optionally add a reason</li>
                  <li>The stop is marked as skipped (no charge)</li>
                </ol>

                <h4>The Importance of Visit ID</h4>
                <div className="note-box">
                  <strong>Key Point:</strong> Each route should have a Visit ID assigned. This is critical for:
                  <ul>
                    <li><strong>Referral Discounts</strong> - Discounts only apply when both referrer and referred customer are serviced in the same visit</li>
                    <li><strong>Reporting</strong> - Track revenue and services per service visit</li>
                    <li><strong>Billing</strong> - Group services for billing purposes</li>
                  </ul>
                </div>

                <h4>Payment Options</h4>
                <p>Select how the customer will pay:</p>
                <ul>
                  <li><strong>Square (Pay Now)</strong> - Process card payment immediately</li>
                  <li><strong>Venmo</strong> - Customer will pay via Venmo</li>
                  <li><strong>Cash</strong> - Customer paid cash on site</li>
                  <li><strong>CashApp</strong> - Customer will pay via CashApp</li>
                  <li><strong>Check</strong> - Customer paid by check</li>
                  <li><strong>Zelle</strong> - Customer will pay via Zelle</li>
                  <li><strong>Unpaid</strong> - Leave unpaid for later invoicing via Bill All</li>
                </ul>

                <h4>Bill All Feature</h4>
                <p>After completing a route, bill all unpaid customers at once:</p>
                <ol>
                  <li>Complete all stops on the route</li>
                  <li>Click <strong>Bill All</strong> on the route card</li>
                  <li>Confirm the action</li>
                  <li>Square invoices are sent to all unpaid customers with email addresses</li>
                </ol>

                <h4>Print Summary</h4>
                <p>Print a route summary showing:</p>
                <ul>
                  <li>Route name and date</li>
                  <li>All stops with service details</li>
                  <li>Payment status for each stop</li>
                  <li>Total revenue</li>
                </ul>

                <h4>Route Completion</h4>
                <p>When all stops are complete or skipped:</p>
                <ol>
                  <li>The route automatically shows as "Completed"</li>
                  <li>Use <strong>Bill All</strong> to invoice remaining unpaid stops</li>
                  <li>Archive the route when fully processed</li>
                </ol>

                <div className="note-box">
                  <strong>Note:</strong> Completed service routes count toward your service visits used for subscription billing.
                </div>
              </AccordionSection>

              <AccordionSection id="billing" title="Billing & Payments">
                <h4>Square Integration</h4>
                <p>Square invoicing allows you to send professional invoices directly from LawnTrack:</p>
                <ul>
                  <li>Invoices are sent via Square with your business info</li>
                  <li>Customers receive email with payment link</li>
                  <li>Accept credit/debit cards online</li>
                  <li>Payment status syncs back to LawnTrack</li>
                </ul>
                <p><strong>Setup:</strong> Square integration is configured by the LawnTrack team. Contact support@lawntrack.pro to enable.</p>

                <h4>Bill All Feature</h4>
                <p>Bill all unpaid customers on a route at once:</p>
                <ol>
                  <li>Complete all stops on the route</li>
                  <li>Click <strong>Bill All</strong> on the route card</li>
                  <li>Review the list of customers to be invoiced</li>
                  <li>Confirm to send all invoices</li>
                </ol>

                <h4>Invoice Line Items</h4>
                <p>Each invoice includes separate line items:</p>
                <ul>
                  <li><strong>Lawn Care</strong> - Base service price</li>
                  <li><strong>Fertilizer</strong> - Salt price (if fertilizer was applied)</li>
                  <li>Any discounts applied</li>
                  <li>Referral discounts (if applicable)</li>
                </ul>

                <h4>Invoice Title & Message Customization</h4>
                <p>Personalize invoices using variables:</p>
                <ul>
                  <li>{'{route_name}'} - Name of the route</li>
                  <li>{'{service_date}'} - Date service was performed</li>
                  <li>{'{customer_name}'} - Customer's name</li>
                </ul>
                <p>Set defaults in Settings, or customize per route.</p>

                <h4>Customer Requirements for Invoicing</h4>
                <p>Customers must have:</p>
                <ul>
                  <li><strong>First and Last Name</strong> - Required for Square customer creation</li>
                  <li><strong>Email Address</strong> - Where invoice is sent</li>
                </ul>
                <p>Customers missing these fields will be skipped during Bill All.</p>

                <h4>Payment Status Tracking</h4>
                <p>Each service has a payment status:</p>
                <ul>
                  <li><strong>Unpaid</strong> - Awaiting payment</li>
                  <li><strong>Paid</strong> - Payment received</li>
                  <li><strong>Partial</strong> - Partial payment received</li>
                </ul>

                <h4>Manual Payment Methods</h4>
                <p>For payments received outside Square:</p>
                <ul>
                  <li><strong>Venmo</strong> - Customer paid via Venmo</li>
                  <li><strong>Cash</strong> - Cash payment received</li>
                  <li><strong>CashApp</strong> - CashApp payment</li>
                  <li><strong>Check</strong> - Check payment</li>
                  <li><strong>Zelle</strong> - Zelle transfer</li>
                </ul>
                <p>Mark as paid in Service Mode when completing stops, or later in Service History.</p>
              </AccordionSection>

              <AccordionSection id="service-history" title="Service History">
                <h4>Viewing History</h4>
                <p>The Service History page shows all completed services. Filter by:</p>
                <ul>
                  <li>Date range</li>
                  <li>Customer</li>
                  <li>Payment status</li>
                </ul>

                <h4>Payment Tracking</h4>
                <ul>
                  <li><strong>Unpaid</strong> - Service completed, awaiting payment</li>
                  <li><strong>Paid</strong> - Payment received</li>
                  <li><strong>Partial</strong> - Partial payment received</li>
                </ul>

                <h4>Recording Payments</h4>
                <ol>
                  <li>Find the service in history</li>
                  <li>Click <strong>Mark Paid</strong></li>
                  <li>Select payment method</li>
                  <li>Payment date recorded automatically</li>
                </ol>
              </AccordionSection>

              <AccordionSection id="expenses" title="Expenses (Pro Tier)">
                <p><em>Available on Pro tier and above.</em></p>

                <h4>Adding Expenses</h4>
                <ol>
                  <li>Navigate to <strong>Expenses</strong></li>
                  <li>Click <strong>+ Add Expense</strong></li>
                  <li>Fill in expense details:
                    <ul>
                      <li><strong>Date</strong> - When the expense occurred</li>
                      <li><strong>Amount</strong> - Cost of the expense</li>
                      <li><strong>Category</strong> - Type of expense</li>
                      <li><strong>Description</strong> - Details about the expense</li>
                      <li><strong>Route</strong> - Optionally link to a specific route</li>
                    </ul>
                  </li>
                  <li>Click <strong>Add Expense</strong></li>
                </ol>

                <h4>Quick Multi-Entry</h4>
                <p>Quickly add multiple expenses:</p>
                <ul>
                  <li><strong>Add & Continue</strong> - Save and keep form open (retains date/route)</li>
                  <li><strong>Add & Close</strong> - Save and close form</li>
                  <li><strong>Update & Add New</strong> - When editing, save and start a new expense</li>
                </ul>

                <h4>Expense Categories</h4>
                <ul>
                  <li><strong>Helper Pay</strong> - Payments to helpers or contractors</li>
                  <li><strong>Materials</strong> - Fertilizer, seed, supplies</li>
                  <li><strong>Fuel</strong> - Gas, diesel for trucks and equipment</li>
                  <li><strong>Equipment</strong> - Tools, mowers, trimmers, parts</li>
                  <li><strong>Repairs</strong> - Maintenance and repair costs</li>
                  <li><strong>Other</strong> - Miscellaneous business expenses</li>
                </ul>

                <h4>Linking to Routes</h4>
                <p>Link expenses to specific routes for better tracking:</p>
                <ul>
                  <li>Select a route when adding an expense</li>
                  <li>Expenses show up in route profitability calculations</li>
                  <li>Filter expenses by route to see costs per job</li>
                </ul>

                <h4>Filtering & Summary</h4>
                <p>Filter your expense list:</p>
                <ul>
                  <li><strong>By Category</strong> - See only specific types</li>
                  <li><strong>By Route</strong> - Expenses for a specific route</li>
                  <li><strong>By Date Range</strong> - Custom start and end dates</li>
                </ul>
                <p>Summary section shows totals by category, updating as you filter.</p>
              </AccordionSection>

              <AccordionSection id="reports" title="Reports (Pro Tier)">
                <p><em>Available on Pro tier and above.</em></p>

                <h4>Accessing Reports</h4>
                <ol>
                  <li>Navigate to <strong>Reports</strong> from the main menu</li>
                  <li>Select a date range preset or custom dates</li>
                  <li>View your Profit & Loss analysis</li>
                </ol>

                <h4>Date Range Options</h4>
                <ul>
                  <li><strong>This Month</strong> - Current calendar month</li>
                  <li><strong>Last Month</strong> - Previous calendar month</li>
                  <li><strong>This Season</strong> - Lawn season (April - October)</li>
                  <li><strong>Custom</strong> - Select specific start and end dates</li>
                </ul>

                <h4>Revenue Breakdown</h4>
                <p>View revenue split by:</p>
                <ul>
                  <li><strong>Payment Status</strong> - Paid vs Unpaid amounts</li>
                  <li><strong>Payment Method</strong> - Square, Venmo, Cash, CashApp, Check, Zelle</li>
                </ul>

                <h4>Expense Breakdown</h4>
                <p>View expenses totaled by category:</p>
                <ul>
                  <li>Helper Pay, Materials, Fuel, Equipment, Repairs, Other</li>
                  <li>Each category shows total spent in the date range</li>
                </ul>

                <h4>Profit Metrics</h4>
                <ul>
                  <li><strong>Total Revenue</strong> - Sum of all completed services</li>
                  <li><strong>Total Expenses</strong> - Sum of all expenses</li>
                  <li><strong>Net Profit</strong> - Revenue minus expenses</li>
                  <li><strong>Profit Margin</strong> - Net profit as percentage of revenue</li>
                </ul>

                <h4>Using Reports</h4>
                <p>Reports help you understand:</p>
                <ul>
                  <li>How much you're making vs spending</li>
                  <li>Which payment methods customers prefer</li>
                  <li>Where your money goes (expense categories)</li>
                  <li>Seasonal trends and profitability</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="settings" title="Settings">
                <h4>Profile Settings</h4>
                <p>Update your personal information:</p>
                <ul>
                  <li><strong>First Name / Last Name</strong></li>
                  <li><strong>Email Address</strong> - Used for login and notifications</li>
                  <li><strong>Phone Number</strong></li>
                  <li><strong>Password</strong> - Click "Change Password" to update</li>
                </ul>

                <h4>Company Information</h4>
                <p>Configure your company details:</p>
                <ul>
                  <li><strong>Company Name</strong> - Appears on invoices and public pages</li>
                  <li><strong>Phone</strong> - Company phone number</li>
                  <li><strong>Email</strong> - Company contact email</li>
                  <li><strong>Address</strong> - Company address</li>
                  <li><strong>Logo</strong> - Upload your logo (appears on invoices)</li>
                  <li><strong>Company Code</strong> - Unique code for your public intake form URL</li>
                </ul>

                <h4>Pricing Tiers</h4>
                <p>Create customizable pricing categories:</p>
                <ol>
                  <li>Go to <strong>Settings → Pricing Tiers</strong></li>
                  <li>Edit existing tiers or click <strong>+ Add Tier</strong></li>
                  <li>For each tier, set:
                    <ul>
                      <li><strong>Name</strong> - e.g., "Small Driveway", "Large Lot"</li>
                      <li><strong>Base Price</strong> - Lawn service price</li>
                      <li><strong>Fertilizer Price</strong> - Salt application price</li>
                    </ul>
                  </li>
                  <li>Click <strong>Save Pricing Tiers</strong></li>
                </ol>
                <p><strong>Note:</strong> Tiers in use by customers are deactivated (not deleted) and can be reactivated.</p>

                <h4>Invoice Defaults</h4>
                <p>Set company-wide invoice defaults (used when routes don't have custom settings):</p>
                <ul>
                  <li><strong>Default Invoice Title</strong> - Title on all invoices</li>
                  <li><strong>Default Invoice Message</strong> - Message body on invoices</li>
                </ul>
                <p>Available variables:</p>
                <ul>
                  <li>{'{route_name}'} - Route name</li>
                  <li>{'{service_date}'} - Date of service</li>
                  <li>{'{customer_name}'} - Customer name</li>
                </ul>

                <h4>Payment Methods</h4>
                <p>Enable or disable payment methods shown in dropdowns:</p>
                <ul>
                  <li>Square, Venmo, Cash, CashApp, Check, Zelle</li>
                  <li>Add custom payment methods for your business</li>
                </ul>

                <h4>Lead Sources</h4>
                <p>Configure how customers find you:</p>
                <ul>
                  <li>Facebook, Google, Yard Sign, Referral, Other</li>
                  <li>Add custom lead sources</li>
                </ul>

                <h4>Referral Discount Settings</h4>
                <p>Configure referral discount program:</p>
                <ul>
                  <li><strong>Enable Referral Discounts</strong> - Turn the feature on/off</li>
                  <li><strong>Discount per Referral</strong> - Amount off per successful referral</li>
                  <li><strong>Minimum Price Floor</strong> - Lowest price after all discounts</li>
                </ul>

                <h4>Weather Location</h4>
                <p>Choose how weather is displayed on your Dashboard:</p>
                <ul>
                  <li><strong>Use GPS Location</strong> - Weather based on your current location</li>
                  <li><strong>Use Home Location</strong> - Set a fixed city/state for weather</li>
                </ul>
                <p>Home location is useful if you're always checking weather for your service area.</p>

                <h4>Feature Requests</h4>
                <p>Submit ideas and track their status:</p>
                <ol>
                  <li>Scroll to <strong>Suggest a Feature</strong></li>
                  <li>Enter subject and description</li>
                  <li>Click <strong>Submit Request</strong></li>
                </ol>
                <p>Track your requests with status updates: New, Under Review, Planned, In Progress, Completed, Declined.</p>
                            </AccordionSection>

              <AccordionSection id="team-management" title="Team Management">
                <h4>User Roles</h4>
                <p>LawnTrack supports multiple user roles with different permissions:</p>
                <ul>
                  <li><strong>Owner</strong> - Full access to all features, billing, and team management</li>
                  <li><strong>Admin</strong> - Full access to operations, cannot manage billing or owner settings</li>
                  <li><strong>Crew Lead</strong> - Can view customers, manage routes, use Service Mode, view history</li>
                  <li><strong>Driver</strong> - Limited access to assigned routes and Service Mode only</li>
                </ul>

                <h4>Inviting Team Members</h4>
                <ol>
                  <li>Go to <strong>Settings → Team Management</strong></li>
                  <li>Click <strong>+ Invite Team Member</strong></li>
                  <li>Enter their email address</li>
                  <li>Select their role (Admin, Crew Lead, or Driver)</li>
                  <li>Click <strong>Send Invitation</strong></li>
                </ol>
                <p>The team member will receive an email with a link to join your team and create their account.</p>

                <h4>Managing Team Members</h4>
                <ul>
                  <li><strong>Change Role</strong> - Update a team member's permissions</li>
                  <li><strong>Remove</strong> - Remove a team member from your account</li>
                  <li><strong>Resend Invitation</strong> - Send another invite to pending members</li>
                </ul>

                <h4>Role Permissions Summary</h4>
                <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '12px'}}>
                  <thead>
                    <tr style={{background: '#f3f4f6'}}>
                      <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Feature</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Owner</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Admin</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Crew Lead</th>
                      <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>Driver</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{padding: '6px 8px'}}>Dashboard</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Customers</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>View</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Routes</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>Assigned</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Service Mode</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>History</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>View</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Expenses</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Reports</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Settings</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>Most</td><td style={{textAlign: 'center'}}>Profile</td><td style={{textAlign: 'center'}}>Profile</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Team Management</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                    <tr><td style={{padding: '6px 8px'}}>Billing</td><td style={{textAlign: 'center'}}>✓</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td><td style={{textAlign: 'center'}}>-</td></tr>
                  </tbody>
                </table>

                <h4>Assigning Routes to Drivers</h4>
                <ol>
                  <li>Open a route for editing</li>
                  <li>Select <strong>Assigned To</strong> dropdown</li>
                  <li>Choose a driver from your team</li>
                  <li>Save the route</li>
                </ol>
                <p>Drivers will only see routes assigned to them in their Routes and Service Mode views.</p>
              </AccordionSection>

              <AccordionSection id="referral-discounts" title="Referral Discounts">
                <h4>How Referrals Work</h4>
                <p>Reward customers who refer new business! When a customer refers someone who also receives
                service, the referring customer gets a discount on their bill.</p>

                <h4>The Importance of Visit ID</h4>
                <p><strong>Key Point:</strong> Referral discounts are tracked <em>per visit</em>. A route must
                have a Visit ID assigned for referral discounts to apply. This ensures discounts are only
                given when both the referrer and referred customer receive service in the same service visit.</p>

                <h4>Setting Up Referral Discounts</h4>
                <ol>
                  <li>Go to <strong>Settings → Referral Discount Settings</strong></li>
                  <li>Enable referral discounts</li>
                  <li>Set the <strong>Discount per Referral</strong> (e.g., $5.00)</li>
                  <li>Set the <strong>Minimum Price Floor</strong> (lowest price after discounts)</li>
                </ol>

                <h4>Example</h4>
                <p>If your discount is set to $5 per referral:</p>
                <ul>
                  <li>Customer A refers Customer B and Customer C</li>
                  <li>All three customers get service during the same visit</li>
                  <li>Customer A receives a $10 discount (2 referrals × $5)</li>
                </ul>
                <p>The minimum price floor prevents discounts from reducing the price below a set amount
                (e.g., $10 minimum ensures you always receive at least $10 per service).</p>

                <h4>Setting Up Customer Referrals</h4>
                <p>When adding or editing a customer, use the "Referred By" dropdown to select which
                existing customer referred them. The system will automatically calculate and apply
                discounts during billing.</p>
              </AccordionSection>
            </div>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <div className="tab-content">
              <h2 className="section-title">Frequently Asked Questions</h2>

              <AccordionSection id="faq-trial" title="How does the free trial work?">
                <p>New accounts start with <strong>1 Free Service Visit</strong>. You have full access to all features
                for your tier. After completing 1 service visit (when a route is marked complete), you'll see
                upgrade prompts to continue using LawnTrack.</p>
              </AccordionSection>

              <AccordionSection id="faq-pricing" title="How does pricing work?">
                <p>LawnTrack uses a hybrid pricing model:</p>
                <ul>
                  <li><strong>Basic</strong>: $10/month + $3 per visit (up to 25 customers)</li>
                  <li><strong>Pro</strong>: $25/month + $2 per visit (up to 75 customers)</li>
                  <li><strong>Premium</strong>: $49/month unlimited (up to 200 customers)</li>
                  <li><strong>Enterprise</strong>: $99/month unlimited (unlimited customers)</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="faq-square" title="How do I set up Square invoicing?">
                <p>Square invoicing is configured by the LawnTrack team. Once enabled, you can:</p>
                <ol>
                  <li>Ensure customers have email addresses</li>
                  <li>Complete stops on a route</li>
                  <li>Click "Bill All" to send invoices</li>
                </ol>
                <p>Invoices are sent through Square with separate line items for service and fertilizer.</p>
              </AccordionSection>

              <AccordionSection id="faq-multiple-properties" title="Can a customer have multiple properties?">
                <p>Yes! Each customer can have multiple service addresses. Each property can have its own:</p>
                <ul>
                  <li>Service address</li>
                  <li>Pricing</li>
                  <li>Salt preference</li>
                  <li>Notes</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="faq-expenses" title="How do I track expenses?">
                <p>Expense tracking is available on Pro tier and above. Navigate to Expenses and:</p>
                <ol>
                  <li>Click + Add Expense</li>
                  <li>Enter date, amount, category</li>
                  <li>Optionally link to a route</li>
                  <li>View summaries by category</li>
                </ol>
              </AccordionSection>

              <AccordionSection id="faq-reports" title="What reports are available?">
                <p>Pro tier includes Profit & Loss reports showing:</p>
                <ul>
                  <li>Revenue by payment status (Paid/Unpaid)</li>
                  <li>Revenue by payment method</li>
                  <li>Expenses by category</li>
                  <li>Net profit and margin</li>
                </ul>
                <p>Filter by This Month, Last Month, This Season, or custom dates.</p>
              </AccordionSection>

              <AccordionSection id="faq-mobile" title="Does LawnTrack work on mobile?">
                <p>Yes! LawnTrack is fully responsive and works great on mobile devices:</p>
                <ul>
                  <li>Pull-to-refresh on all pages</li>
                  <li>Touch-friendly interface</li>
                  <li>Add to home screen for app-like experience (PWA)</li>
                  <li>Tap phone numbers to call or text</li>
                </ul>
              </AccordionSection>

              <AccordionSection id="faq-intake" title="Can customers sign up themselves?">
                <p>Yes! Share your public intake form URL:</p>
                <code>https://lawntrack.pro/signup/YOUR-COMPANY-CODE</code>
                <p>Find your company code in Settings. Customers can fill out their information and
                submit a request for service.</p>
              </AccordionSection>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="tab-content">
              <h2 className="section-title">Contact & Support</h2>

              <div className="contact-grid">
                <div className="contact-card">
                  <h3>Submit a Feature Request</h3>
                  <p>Have an idea to improve LawnTrack? We'd love to hear it!</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/settings')}
                  >
                    Go to Settings → Feature Requests
                  </button>
                </div>

                <div className="contact-card">
                  <h3>Email Support</h3>
                  <p>For technical issues or account questions:</p>
                  <a href="mailto:support@lawntrack.pro" className="contact-link">
                    support@lawntrack.pro
                  </a>
                </div>

                <div className="contact-card">
                  <h3>Track Your Requests</h3>
                  <p>View the status of your submitted feature requests in Settings.</p>
                  <p className="status-legend">
                    <span className="status-badge new">New</span>
                    <span className="status-badge under-review">Under Review</span>
                    <span className="status-badge planned">Planned</span>
                    <span className="status-badge in-progress">In Progress</span>
                    <span className="status-badge completed">Completed</span>
                  </p>
                </div>
              </div>

              <div className="tip-box" style={{ marginTop: '24px' }}>
                <strong>Coming Soon:</strong> LawnTrack - The same powerful features for lawn care,
                launching Spring 2026. One account, year-round coverage!
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="help-footer no-print">
        <p>LawnTrack™ v2.8.0 • © {new Date().getFullYear()}</p>
      </footer>

      <style>{`
        .help-page {
          min-height: 100vh;
          background: var(--color-background, #f5f5f5);
        }

        /* Header - matches Dashboard.js exactly (uses global .app-header from index.css) */
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .help-link {
          width: 28px !important;
          height: 28px !important;
          padding: 0 !important;
          border-radius: 50% !important;
          font-weight: bold !important;
          font-size: 1rem !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: rgba(255,255,255,0.15) !important;
        }
        .help-link:hover, .help-link.active {
          background: rgba(255,255,255,0.25) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
        }

        /* Main Content */
        .help-main {
          max-width: 1000px;
          margin: 0 auto;
          padding: 24px 20px;
        }

        .help-title-section {
          text-align: center;
          margin-bottom: 24px;
        }

        .help-title {
          font-size: 2rem;
          color: #4CAF50;
          margin: 0 0 8px 0;
        }

        .help-subtitle {
          color: #6b7280;
          margin: 0;
        }

        /* Search and Actions */
        .help-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          gap: 16px;
          flex-wrap: wrap;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-input {
          width: 100%;
          padding: 10px 36px 10px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
        }

        .search-input:focus {
          outline: none;
          border-color: #4CAF50;
          box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.1);
        }

        .clear-search {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          font-size: 1.25rem;
          color: #9ca3af;
          cursor: pointer;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        /* Tabs */
        .help-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .help-tab {
          padding: 10px 20px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }

        .help-tab:hover {
          background: #f9fafb;
          color: #374151;
        }

        .help-tab.active {
          background: #4CAF50;
          color: white;
          border-color: #4CAF50;
        }

        /* Content */
        .help-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        .section-title {
          font-size: 1.5rem;
          color: #4CAF50;
          margin: 0 0 16px 0;
        }

        .intro-text {
          color: #4b5563;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        /* Steps Grid */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .step-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
        }

        .step-number {
          width: 32px;
          height: 32px;
          background: #4CAF50;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .step-card h3 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0 0 8px 0;
        }

        .step-card p {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 12px 0;
          line-height: 1.5;
        }

        /* Accordion */
        .accordion-section {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 12px;
          overflow: hidden;
        }

        .accordion-header {
          width: 100%;
          padding: 16px 20px;
          background: #f9fafb;
          border: none;
          text-align: left;
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: background 0.15s;
        }

        .accordion-header:hover {
          background: #f3f4f6;
        }

        .accordion-header.expanded {
          background: #4CAF50;
          color: white;
        }

        .accordion-icon {
          font-size: 1.25rem;
          font-weight: 400;
        }

        .accordion-content {
          padding: 20px;
          background: white;
        }

        .accordion-content h4 {
          font-size: 0.95rem;
          color: #4CAF50;
          margin: 16px 0 8px 0;
        }

        .accordion-content h4:first-child {
          margin-top: 0;
        }

        .accordion-content p {
          color: #4b5563;
          line-height: 1.6;
          margin: 0 0 12px 0;
        }

        .accordion-content ul, .accordion-content ol {
          color: #4b5563;
          margin: 0 0 12px 0;
          padding-left: 24px;
        }

        .accordion-content li {
          margin-bottom: 6px;
          line-height: 1.5;
        }

        .accordion-content code {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.85rem;
        }

        /* Tip Box */
        .tip-box {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          padding: 16px;
          color: #92400e;
        }

        .note-box {
          background: #dbeafe;
          border: 1px solid #93c5fd;
          border-radius: 8px;
          padding: 16px;
          color: #1e40af;
          margin-top: 12px;
        }

        /* Contact Grid */
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .contact-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
        }

        .contact-card h3 {
          font-size: 1rem;
          color: #1f2937;
          margin: 0 0 8px 0;
        }

        .contact-card p {
          color: #6b7280;
          margin: 0 0 12px 0;
        }

        .contact-link {
          color: #4CAF50;
          font-weight: 500;
          text-decoration: none;
        }

        .contact-link:hover {
          text-decoration: underline;
        }

        .status-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }

        .status-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 500;
        }

        .status-badge.new { background: #dbeafe; color: #1e40af; }
        .status-badge.under-review { background: #fef3c7; color: #92400e; }
        .status-badge.planned { background: #ede9fe; color: #6d28d9; }
        .status-badge.in-progress { background: #ffedd5; color: #c2410c; }
        .status-badge.completed { background: #d1fae5; color: #065f46; }

        /* Buttons */
        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 0.8rem;
        }

        .btn-primary {
          background: #4CAF50;
          color: white;
        }

        .btn-primary:hover {
          background: #2d4a73;
        }

        .btn-secondary {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-secondary:hover {
          background: #d1d5db;
        }

        /* Full Manual View */
        .full-manual {
          max-width: 800px;
          margin: 0 auto;
        }

        .full-manual-header {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .manual-title-section {
          text-align: center;
          margin-bottom: 32px;
        }

        .manual-title-section h1 {
          font-size: 2rem;
          color: #4CAF50;
          margin: 0 0 8px 0;
        }

        .manual-version {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .manual-toc {
          background: #f9fafb;
          padding: 24px;
          border-radius: 8px;
          margin-bottom: 32px;
        }

        .manual-toc h2 {
          margin: 0 0 16px 0;
          font-size: 1.25rem;
          color: #374151;
        }

        .manual-toc ol {
          margin: 0;
          padding-left: 24px;
        }

        .manual-toc li {
          margin-bottom: 8px;
        }

        .manual-toc a {
          color: #4CAF50;
          text-decoration: none;
        }

        .manual-toc a:hover {
          text-decoration: underline;
        }

        .manual-section {
          margin-bottom: 48px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .manual-section h2 {
          font-size: 1.5rem;
          color: #4CAF50;
          margin: 0 0 24px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #4CAF50;
        }

        .manual-section h3 {
          font-size: 1.1rem;
          color: #374151;
          margin: 24px 0 12px 0;
        }

        .manual-section p {
          margin: 0 0 12px 0;
          line-height: 1.6;
          color: #4b5563;
        }

        .manual-section ul, .manual-section ol {
          margin: 0 0 16px 0;
          padding-left: 24px;
        }

        .manual-section li {
          margin-bottom: 6px;
          line-height: 1.5;
          color: #4b5563;
        }

        .manual-section code {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
        }

        .manual-footer {
          text-align: center;
          padding: 32px 0;
          color: #9ca3af;
          font-size: 0.85rem;
        }

        /* Footer */
        .help-footer {
          text-align: center;
          padding: 20px;
          color: #9ca3af;
          font-size: 0.85rem;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex;
          }

          .help-actions {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .search-box {
            max-width: none;
          }

          .action-buttons {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .action-buttons .btn {
            width: 100%;
            min-height: 44px;
            font-size: 1rem;
          }

          .help-tabs {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .help-tab {
            min-height: 44px;
            padding: 12px 8px;
            font-size: 0.85rem;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .help-title {
            font-size: 1.5rem;
          }

          .help-subtitle {
            font-size: 0.9rem;
          }

          .steps-grid {
            grid-template-columns: 1fr;
          }

          .full-manual-header {
            flex-direction: column;
            gap: 8px;
          }

          .full-manual-header .btn {
            width: 100%;
            min-height: 44px;
          }
        }

        /* Print Styles */
        @media print {
          .no-print {
            display: none !important;
          }

          .help-page {
            background: white;
          }

          .help-main {
            padding: 0;
            max-width: none;
          }

          .help-content {
            box-shadow: none;
            border: none;
          }

          .accordion-section {
            border: 1px solid #ccc;
            break-inside: avoid;
          }

          .accordion-header {
            background: #f0f0f0 !important;
            color: black !important;
          }

          .accordion-content {
            display: block !important;
          }

          .step-card {
            break-inside: avoid;
          }

          .btn {
            display: none;
          }

          /* Full Manual Print Styles */
          .full-manual {
            max-width: none;
          }

          .manual-title-section h1 {
            color: black;
          }

          .manual-toc {
            background: #f5f5f5;
            page-break-after: always;
          }

          .manual-section {
            page-break-inside: avoid;
          }

          .manual-section h2 {
            color: black;
            border-bottom-color: #333;
          }
        }
      `}</style>
    </div>
  );
}

export default Help;
